import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import dayjs from 'dayjs';
import { createProvider } from '@frontend/store';
import { DATETIME_FORMATS } from '../constants';
import { CustomProviderType } from '../hooks/booking-site';
import { startOfWeek } from '../utils/date-helpers';

interface State {
  isSchedularV3Enabled: boolean;

  selectedLocationId: string;
  selectedLocationName: string;
  selectedLocationTimeZone: string;
  paymentLocationPortalSlug: string;

  selectedAppointmentType?: AppointmentType;
  selectedAppointmentLeadTime: string; // ISO date string
  weekInView: string; // ISO date string
  weekAnchorDate: string;
  minSlots: number;

  availableProviders: CustomProviderType[];
  allProviderAvailabilities: Record<string, string[]>; // providerId, [slots] (slots are ISO date strings)
  selectedProvider?: CustomProviderType | 'any';
  selectedDay?: string;

  selectedSlots: string[];
}

interface BookingSiteStore extends State {
  setIsSchedularV3Enabled: (isSchedularV3Enabled: boolean) => void;

  setSelectedLocation: (
    locationId: string,
    locationName: string,
    paymentLocationPortalSlug: string,
    selectedLocationTimeZone: string
  ) => void;

  setSelectedAppointmentType: (
    appointmentType: AppointmentType,
    isSchedularV3Enabled: boolean,
    minSlots?: number
  ) => void;
  clearSelectedAppointmentType: () => void;

  setWeekInView: (weekInView: string) => void;
  setWeekAnchorDate: (weekAnchorDate: string) => void;

  setAllProviderAvailabilities: (providerId: string, slots: string[]) => void;
  setAvailableProviders: (providers: CustomProviderType[]) => void;
  setSelectedProvider: (provider: CustomProviderType | 'any', selectedDay?: string) => void;
  clearSelectedProvider: () => void;

  setSelectedSlots: (slots: string[]) => void;
}

export const { Provider: BookingSiteStoreProvider, useStore: useBookingSiteStore } = createProvider<BookingSiteStore>()(
  (set, get) => ({
    isSchedularV3Enabled: false,
    setIsSchedularV3Enabled: (isSchedularV3Enabled) => {
      set({ isSchedularV3Enabled });
    },

    selectedLocationId: '',
    selectedLocationName: '',
    selectedLocationTimeZone: '',
    paymentLocationPortalSlug: '',
    setSelectedLocation: (
      selectedLocationId,
      selectedLocationName,
      paymentLocationPortalSlug,
      selectedLocationTimeZone
    ) => {
      // reset availability on location change to avoid showing stale data
      set({
        selectedLocationId,
        selectedLocationName,
        paymentLocationPortalSlug,
        allProviderAvailabilities: {},
        selectedLocationTimeZone,
      });
    },

    selectedAppointmentType: undefined,
    selectedAppointmentLeadTime: dayjs().toISOString(),
    minSlots: 3,
    setSelectedAppointmentType: (selectedAppointmentType, isSchedularV3Enabled, minSlots = 3) => {
      // Scheduler V3 uses minutes for lead time and is named 'leadDuration', while V2 uses seconds and is named 'requestBufferDuration'.
      const requestBufferDurationTimeUnit = isSchedularV3Enabled ? 'minutes' : 'seconds';
      const selectedAppointmentLeadTime = dayjs()
        .add(selectedAppointmentType?.requestBufferDuration ?? 0, requestBufferDurationTimeUnit)
        .toISOString();

      const locationTimezone = get().selectedLocationTimeZone || '';
      const leadDateTimeObj = isSchedularV3Enabled
        ? dayjs.tz(selectedAppointmentLeadTime, locationTimezone)
        : dayjs(selectedAppointmentLeadTime);
      const todayStartDateTimeObj = isSchedularV3Enabled
        ? dayjs().tz(locationTimezone).startOf('day')
        : dayjs().startOf('day');

      const weekAnchorDateTimeObj = selectedAppointmentType ? leadDateTimeObj : todayStartDateTimeObj;
      const weekStartDateObj = startOfWeek(weekAnchorDateTimeObj);
      const weekInView = weekStartDateObj.isBefore(leadDateTimeObj)
        ? leadDateTimeObj.toISOString()
        : weekStartDateObj.toISOString();

      set({
        selectedAppointmentType,
        selectedAppointmentLeadTime: selectedAppointmentType ? selectedAppointmentLeadTime : dayjs().toISOString(),
        weekAnchorDate: weekAnchorDateTimeObj.format(DATETIME_FORMATS.US_DATE),
        weekInView,
        minSlots,
        selectedProvider: undefined,
      });
    },
    clearSelectedAppointmentType: () => {
      set({
        selectedAppointmentType: undefined,
        selectedAppointmentLeadTime: dayjs().toISOString(),
        availableProviders: [],
        weekAnchorDate: dayjs().format(DATETIME_FORMATS.US_DATE),
        weekInView: dayjs().toISOString(),
        selectedProvider: undefined,
        selectedDay: undefined,
        selectedSlots: [],
      });
    },

    weekInView: dayjs().toISOString(),
    setWeekInView: (weekInView) => set({ weekInView }),

    weekAnchorDate: dayjs().format(DATETIME_FORMATS.US_DATE),
    setWeekAnchorDate: (weekAnchorDate) => set({ weekAnchorDate }),

    availableProviders: [],
    selectedProvider: undefined,
    selectedDay: undefined,
    allProviderAvailabilities: {},
    setAllProviderAvailabilities: (providerId, slots) => {
      set((state) => ({ allProviderAvailabilities: { ...state.allProviderAvailabilities, [providerId]: slots } }));
    },
    setAvailableProviders: (availableProviders) => {
      set({ availableProviders });
    },
    setSelectedProvider: (selectedProvider, selectedDay) => {
      set({ selectedProvider, selectedDay });
    },
    clearSelectedProvider: () => {
      set({ selectedProvider: undefined, selectedDay: undefined, selectedSlots: [] });
    },

    selectedSlots: [],
    setSelectedSlots: (selectedSlots) => {
      set({ selectedSlots });
    },
  })
);
